/**
 * usePaywallCancellation - Hook for managing cancellation flow within the paywall
 *
 * Provides mutations for:
 * - Creating a cancellation request
 * - Fetching winback pricing
 * - Accepting winback offer
 * - Declining winback offer (cancelling subscription)
 */

import { useState, useCallback } from 'react';
import {
  useCreateCancellationRequestV1SubscriptionCancellationRequestsPost,
  useGetWinbackPricingV1SubscriptionCancellationRequestsStripeSubscriptionIdWinbackPricingGet,
  useAcceptWinbackOfferV1SubscriptionCancellationRequestsAcceptWinbackPost,
  useDeclineWinbackOfferV1SubscriptionCancellationRequestsDeclineWinbackPost,
} from '@/api/generated-catch-backend/subscriptions/subscriptions';
import type {
  CancellationReason,
  CancellationRequestResponse,
  WinbackPricingResponse,
} from '@/api/generated-catch-backend/model';

export interface UsePaywallCancellationResult {
  /** Current cancellation request (after submitting reason) */
  cancellationRequest: CancellationRequestResponse | null;
  /** Winback pricing data */
  winbackPricing: WinbackPricingResponse | null;
  /** Whether pricing is loading */
  isPricingLoading: boolean;
  /** Whether any mutation is in progress */
  isSubmitting: boolean;
  /** Error message if any operation failed */
  error: string | null;
  /** Submit cancel reason and get winback offer */
  submitCancelReason: (
    subscriptionId: string,
    reason: CancellationReason,
    reasonDescription?: string
  ) => Promise<void>;
  /** Accept the winback offer */
  acceptWinback: (subscriptionId: string) => Promise<void>;
  /** Decline winback and cancel subscription */
  declineWinback: (subscriptionId: string) => Promise<void>;
  /** Reset the cancellation state */
  reset: () => void;
}

export function usePaywallCancellation(
  subscriptionId: string | null
): UsePaywallCancellationResult {
  const [cancellationRequest, setCancellationRequest] =
    useState<CancellationRequestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch winback pricing when we have a subscription ID
  const {
    data: winbackPricing,
    isLoading: isPricingLoading,
    refetch: refetchPricing,
  } = useGetWinbackPricingV1SubscriptionCancellationRequestsStripeSubscriptionIdWinbackPricingGet(
    subscriptionId ?? '',
    {
      query: {
        enabled: !!subscriptionId && !!cancellationRequest,
        retry: false,
      },
    }
  );

  // Mutations
  const createCancellationMutation =
    useCreateCancellationRequestV1SubscriptionCancellationRequestsPost();
  const acceptWinbackMutation =
    useAcceptWinbackOfferV1SubscriptionCancellationRequestsAcceptWinbackPost();
  const declineWinbackMutation =
    useDeclineWinbackOfferV1SubscriptionCancellationRequestsDeclineWinbackPost();

  const isSubmitting =
    createCancellationMutation.isPending ||
    acceptWinbackMutation.isPending ||
    declineWinbackMutation.isPending;

  const submitCancelReason = useCallback(
    async (
      subId: string,
      reason: CancellationReason,
      reasonDescription?: string
    ) => {
      setError(null);
      try {
        const result = await createCancellationMutation.mutateAsync({
          data: {
            stripeSubscriptionId: subId,
            reason,
            reasonDescription: reasonDescription || null,
          },
        });
        setCancellationRequest(result);
        // Trigger pricing fetch
        await refetchPricing();
      } catch (err) {
        setError('Failed to submit cancellation request. Please try again.');
        throw err;
      }
    },
    [createCancellationMutation, refetchPricing]
  );

  const acceptWinback = useCallback(
    async (subId: string) => {
      if (!cancellationRequest) {
        setError('No cancellation request found');
        return;
      }
      setError(null);
      try {
        await acceptWinbackMutation.mutateAsync({
          data: {
            cancellationRequestId:
              cancellationRequest.subscriptionCancellationRequestId,
            stripeSubscriptionId: subId,
          },
        });
      } catch (err: unknown) {
        // Check if subscription is already canceled (409 with specific code)
        // If so, reload to show the correct paywall flow (create_subscription)
        const axiosError = err as { response?: { status?: number; data?: { code?: string } } };
        if (
          axiosError?.response?.status === 409 &&
          axiosError?.response?.data?.code === 'SUBSCRIPTION_ALREADY_CANCELED'
        ) {
          window.location.reload();
          return; // Don't throw - we're handling it by reloading
        }
        setError('Failed to apply discount. Please try again.');
        throw err;
      }
    },
    [cancellationRequest, acceptWinbackMutation]
  );

  const declineWinback = useCallback(
    async (subId: string) => {
      if (!cancellationRequest) {
        setError('No cancellation request found');
        return;
      }
      setError(null);
      try {
        await declineWinbackMutation.mutateAsync({
          data: {
            cancellationRequestId:
              cancellationRequest.subscriptionCancellationRequestId,
            stripeSubscriptionId: subId,
          },
        });
      } catch (err) {
        setError('Failed to cancel subscription. Please try again.');
        throw err;
      }
    },
    [cancellationRequest, declineWinbackMutation]
  );

  const reset = useCallback(() => {
    setCancellationRequest(null);
    setError(null);
  }, []);

  return {
    cancellationRequest,
    winbackPricing: winbackPricing ?? null,
    isPricingLoading,
    isSubmitting,
    error,
    submitCancelReason,
    acceptWinback,
    declineWinback,
    reset,
  };
}
